import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { catchError, tap } from 'rxjs';
import { AdminOrderRoutes } from 'src/app/core/enums/routes.enum';
import { ApiResponse } from 'src/app/core/models/api-response';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { ResetCurrentFilter } from 'src/app/core/state/main-table-filter.actions';
import { ISfiCodeResponse } from '../models/sfi-code.model';
import { SfiCodeApiService } from '../services/sfi-code-api.service';
import {
  CreateSfiCode,
  DeleteSfiCode,
  LoadSfiCodeById,
  LoadSfiCodes,
  LoadAllSfiCodes,
  LoadAllSfiCodesPerDepartment,
  UpdateSfiCode,
} from './sfi-code.actions';

export interface SfiCodeStateModel {
  sfiCodes: ISfiCodeResponse[];
  allSfiCodes: ISfiCodeResponse[];
  allSfiCodesPerDepartment: ISfiCodeResponse[];
  currentSfiCode: ISfiCodeResponse | undefined;
  sfiCodesTotalPages: number;
}

const SFI_CODE_STATE_TOKEN = new StateToken<SfiCodeStateModel>('sfiCode');

@State<SfiCodeStateModel>({
  name: SFI_CODE_STATE_TOKEN,
  defaults: {
    sfiCodes: [],
    allSfiCodes: [],
    allSfiCodesPerDepartment: [],
    sfiCodesTotalPages: 0,
    currentSfiCode: undefined,
  },
})
@Injectable()
export class SfiCodeState {
  constructor(private service: SfiCodeApiService) {}

  @Action(LoadSfiCodes, { cancelUncompleted: true })
  loadSfiCodes({ getState, setState }: StateContext<SfiCodeStateModel>, { filter }: LoadSfiCodes) {
    return this.service.loadSfiCodes(filter).pipe(
      tap((response: ApiResponse<ISfiCodeResponse[]>) => {
        const state = getState();
        setState({
          ...state,
          sfiCodes: response.data,
          sfiCodesTotalPages: response.totalPages,
        });
      }),
      catchError((error) => {
        console.error('Failed to load sfi codes:', error);
        throw error;
      })
    );
  }

  @Action(LoadAllSfiCodes, { cancelUncompleted: true })
  loadAllSfiCodes({ setState }: StateContext<SfiCodeStateModel>) {
    return this.service.loadAllSfiCodes().pipe(
      tap((allSfiCodes: ISfiCodeResponse[]) => {
        setState(patch({ allSfiCodes }));
      }),
      catchError((error) => {
        console.error('Failed to load all sfi codes:', error);
        throw error;
      })
    );
  }

  @Action(LoadAllSfiCodesPerDepartment, { cancelUncompleted: true })
  loadAllSfiCodesPerDepartment({ setState }: StateContext<SfiCodeStateModel>) {
    return this.service.loadAllSfiCodesPerDepartment().pipe(
      tap((allSfiCodesPerDepartment: ISfiCodeResponse[]) => {
        setState(patch({ allSfiCodesPerDepartment }));
      }),
      catchError((error) => {
        console.error('Failed to load all sfi codes per department:', error);
        throw error;
      })
    );
  }

  @Action(LoadSfiCodeById, { cancelUncompleted: true })
  loadSfiCodeById({ setState }: StateContext<SfiCodeStateModel>, { id }: LoadSfiCodeById) {
    return this.service.loadSfiCodeById(id).pipe(
      tap((sfiCode: ISfiCodeResponse) => {
        setState(
          patch({
            currentSfiCode: sfiCode,
          })
        );
      }),
      catchError((error) => {
        console.error('Failed to load sfi code by id:', error);
        throw error;
      })
    );
  }

  @Action(CreateSfiCode, { cancelUncompleted: true })
  @OnSuccess({ url: AdminOrderRoutes.SfiCodes, message: 'sfiCodeCreated' })
  createSfiCode({}: StateContext<SfiCodeStateModel>, { sfiCode }: CreateSfiCode) {
    return this.service.createSfiCode(sfiCode).pipe(
      catchError((error) => {
        console.error('Failed to create sfi code:', error);
        throw error;
      })
    );
  }

  @Action(UpdateSfiCode, { cancelUncompleted: true })
  @OnSuccess({ url: AdminOrderRoutes.SfiCodes, message: 'sfiCodeUpdated' })
  updateSfiCode({}: StateContext<SfiCodeStateModel>, { sfiCode }: UpdateSfiCode) {
    return this.service.updateSfiCode(sfiCode).pipe(
      catchError((error) => {
        console.error('Failed to update sfi code:', error);
        throw error;
      })
    );
  }

  @Action(DeleteSfiCode, { cancelUncompleted: true })
  @OnSuccess({
    url: AdminOrderRoutes.SfiCodes,
    message: 'sfiCodeDeleted',
    dispatch: ResetCurrentFilter,
  })
  deleteSfiCode({}: StateContext<SfiCodeStateModel>, { id }: DeleteSfiCode) {
    return this.service.deleteSfiCode(id).pipe(
      catchError((error) => {
        console.error('Failed to delete sfi code:', error);
        throw error;
      })
    );
  }

  @Selector()
  static getSfiCodesTotalPages(state: SfiCodeStateModel) {
    return state.sfiCodesTotalPages;
  }

  @Selector()
  static sfiCodes(state: SfiCodeStateModel) {
    return state.sfiCodes;
  }

  @Selector()
  static allSfiCodes(state: SfiCodeStateModel) {
    return state.allSfiCodes;
  }

  @Selector()
  static allSfiCodesPerDepartment(state: SfiCodeStateModel) {
    return state.allSfiCodesPerDepartment;
  }

  @Selector()
  static getCurrentSfiCode(state: SfiCodeStateModel) {
    return state.currentSfiCode;
  }
}
